import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    ButtonGroup,
    Card,
    Col,
    Row,
    Modal,
    Badge,
    Form,
    InputGroup,
    Spinner,
    Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHistory,
    faEye,
    faCalendarAlt,
    faUser,
    faFileAlt,
    faMoneyBillWave,
    faSearch,
    faFilter,
    faSync,
    faCheckCircle,
    faClock,
    faExclamationTriangle,
    faTimesCircle,
    faPlay,
    faUserCheck,
    faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { fetchApi, getSector } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import CustomTable from "../../components/CustomTable";
import CustomTypeahead from "../components/CustomTypeahead";
import moment from "moment";
import { Routes } from "../../routes";
import { useNavigate } from "react-router-dom";

// Enhanced status information for Payment Schedules
const getStatusInfo = (status) => {
    const statusLower = status?.toLowerCase() || "";

    const statusMap = {
        'initiated': {
            title: 'Schedule Initiated',
            description: 'Payment schedule has been initiated and is awaiting processing',
            icon: faPlay,
            type: 'info'
        },
        'pending': {
            title: 'Pending Review',
            description: 'Awaiting review and verification by the next level',
            icon: faClock,
            type: 'warning'
        },
        'approved': {
            title: 'Approved',
            description: 'Schedule has been approved and is ready for payment',
            icon: faCheckCircle,
            type: 'success'
        },
        'paid': {
            title: 'Paid',
            description: 'Payment has been successfully completed',
            icon: faHandshake,
            type: 'success'
        },
        'returned': {
            title: 'Returned',
            description: 'Schedule has been returned for corrections',
            icon: faExclamationTriangle,
            type: 'warning'
        },
        'rejected': {
            title: 'Rejected',
            description: 'Schedule has been rejected',
            icon: faTimesCircle,
            type: 'danger'
        }
    };

    if (statusMap[statusLower]) {
        return statusMap[statusLower];
    }

    for (const [key, value] of Object.entries(statusMap)) {
        if (statusLower.includes(key)) {
            return value;
        }
    }

    return {
        title: status || 'Unknown Status',
        description: 'Status information is being processed',
        icon: faFileAlt,
        type: 'secondary'
    };
};

// Generate progress steps
const getProgressSteps = (statusHistory) => {
    const commonSteps = [
        { key: 'initiated', label: 'Initiated', icon: faPlay },
        { key: 'pending', label: 'Pending', icon: faClock },
        { key: 'approved', label: 'Approved', icon: faCheckCircle },
        { key: 'paid', label: 'Paid', icon: faHandshake }
    ];

    const historyStatuses = statusHistory.map(h => h.status.toLowerCase());
    const currentStatus = historyStatuses[historyStatuses.length - 1];

    return commonSteps.map(step => {
        if (historyStatuses.includes(step.key)) {
            return { ...step, status: step.key === currentStatus ? 'current' : 'completed' };
        } else if (step.key === 'initiated') {
            return { ...step, status: 'completed' };
        } else {
            return { ...step, status: 'pending' };
        }
    });
};

function AllPaymentScheduleHistory() {
    const history = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Draft states — bound to inputs, never trigger API
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [mdaFilter, setMdaFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // Committed states — only updated on "Apply Filters" or "Clear"
    const [appliedSearch, setAppliedSearch] = useState("");
    const [appliedStatus, setAppliedStatus] = useState("");
    const [appliedMda, setAppliedMda] = useState("");
    const [appliedDateFrom, setAppliedDateFrom] = useState("");
    const [appliedDateTo, setAppliedDateTo] = useState("");

    // Pagination
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        page: 1, limit: 50, total: 0, totalPages: 0
    });

    const [mdaOptions, setMdaOptions] = useState([]);
    useEffect(() => {
        getSector({ type: "mdas" }, "mdas", (data) => {
            setMdaOptions(
                (data || []).map((i) => ({ mda_name: i.description, mda_code: i.head }))
            );
        });
    }, []);

    const fetchData = useCallback(() => {
        setLoading(true);

        const paramObj = {
            page,
            limit: 50,
            userRole: user?.role || 'USER',
            userMdaCode: user?.mda_code || '',
            userId: user?.username || '',
        };
        if (appliedSearch.trim()) paramObj.search = appliedSearch.trim();
        if (appliedStatus) paramObj.status = appliedStatus;
        if (appliedMda.trim()) paramObj.mda_name = appliedMda.trim();
        if (appliedDateFrom) paramObj.date_from = appliedDateFrom;
        if (appliedDateTo) paramObj.date_to = appliedDateTo;

        const params = new URLSearchParams(paramObj);

        fetchApi(`get_all_payment_schedule_history?${params.toString()}`)
            .then((data) => {
                if (data.success) {
                    setList(data.result || []);
                    setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
                }
            })
            .catch((err) => {
                console.error("Error fetching payment schedule history:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, appliedSearch, appliedStatus, appliedMda, appliedDateFrom, appliedDateTo, user?.role, user?.mda_code, user?.username]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleApplyFilters = () => {
        setAppliedSearch(search);
        setAppliedStatus(statusFilter);
        setAppliedMda(mdaFilter);
        setAppliedDateFrom(dateFrom);
        setAppliedDateTo(dateTo);
        setPage(1);
    };

    const handleClearFilters = () => {
        setSearch("");
        setStatusFilter("");
        setMdaFilter("");
        setDateFrom("");
        setDateTo("");
        setAppliedSearch("");
        setAppliedStatus("");
        setAppliedMda("");
        setAppliedDateFrom("");
        setAppliedDateTo("");
        setPage(1);
    };

    const handleViewHistory = (schedule) => {
        setSelectedSchedule(schedule);
        setShowHistoryModal(true);
    };

    const getStatusBadgeVariant = (status) => {
        const statusLower = status?.toLowerCase() || "";
        if (statusLower.includes("approved") || statusLower.includes("paid")) {
            return "success";
        } else if (statusLower.includes("pending") || statusLower.includes("initiated")) {
            return "warning";
        } else if (statusLower.includes("returned") || statusLower.includes("rejected")) {
            return "danger";
        }
        return "primary";
    };

    const renderPagination = () => {
        if (pagination.totalPages <= 1) return null;

        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => setPage(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        for (let p = startPage; p <= endPage; p++) {
            items.push(
                <Pagination.Item
                    key={p}
                    active={p === page}
                    onClick={() => setPage(p)}
                >
                    {p}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.totalPages) {
            if (endPage < pagination.totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.totalPages}
                    onClick={() => setPage(pagination.totalPages)}
                >
                    {pagination.totalPages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3 p-3">
                <div className="text-muted">
                    Showing {((page - 1) * pagination.limit) + 1} to{" "}
                    {Math.min(page * pagination.limit, pagination.total)} of{" "}
                    {pagination.total} entries
                </div>
                <Pagination>
                    <Pagination.Prev
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={page === pagination.totalPages}
                        onClick={() => setPage(p => p + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    const fields = [
        {
            title: "Batch No",
            value: "batch_id",
        },
        {
            title: "MDA Name",
            value: "mda_name",
        },
        {
            title: "Contractor",
            value: "contractor",
        },
        {
            title: "Amount",
            custom: true,
            component: (item) => (
                <div className="text-end">{formatNumber(item.amount)}</div>
            ),
        },
        {
            title: "Current Status",
            custom: true,
            component: (item) => (
                <Badge bg={getStatusBadgeVariant(item.current_status)}>
                    {item.current_status}
                </Badge>
            ),
        },
        {
            title: "Date",
            custom: true,
            component: (item) => (
                <div>{moment(item.date).format("DD/MM/YYYY")}</div>
            ),
        },
        {
            title: "Actions",
            custom: true,
            component: (item) => (
                <div className="d-flex gap-2">
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleViewHistory(item)}
                    >
                        <FontAwesomeIcon icon={faHistory} className="me-1" />
                        Journey
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                            history(`${Routes.expenditureControl.dirExpPaymentScheduleView}/${item.batch_id}?viewOnly=true&status=${item.current_status}`);
                        }}
                    >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        View
                    </Button>
                </div>
            ),
        },
    ];

    console.log("paymentScheduleHistory", selectedSchedule);

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                <div className="d-block mb-4 mb-md-0">
                    <h4>
                        <FontAwesomeIcon icon={faHistory} className="me-2" />
                        Payment Schedule Report Tracking
                    </h4>
                    <p className="mb-0">Track payment schedules and their status history</p>
                </div>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <ButtonGroup>
                        <Button variant="outline-primary" size="sm" onClick={fetchData}>
                            <FontAwesomeIcon icon={faSync} className="me-1" />
                            Refresh
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            <Card border="light" className="shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Search Schedules</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Batch No, MDA, Contractor..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                    />
                                    <Button variant="outline-primary" onClick={handleApplyFilters}>
                                        <FontAwesomeIcon icon={faSearch} />
                                    </Button>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Search by using Batch No, MDA, Contractor
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Initiated">Initiated</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Returned">Returned</option>
                                    <option value="Rejected">Rejected</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>MDA Name</Form.Label>
                                <CustomTypeahead
                                    labelKey="mda_name"
                                    options={mdaOptions}
                                    selected={mdaFilter ? [{ mda_name: mdaFilter }] : []}
                                    onChange={(s) => setMdaFilter(s.length ? s[0].mda_name : "")}
                                    placeholder="Search MDA name..."
                                    disabled={user?.role === 'MDA ADMIN'}
                                />
                                {user?.role === 'MDA ADMIN' ? (
                                    <Form.Text className="text-muted">
                                        Restricted to your MDA: {user?.mda_name}
                                    </Form.Text>
                                ) : (
                                    <Form.Text className="text-muted">
                                        Leave empty to search across all MDAs
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Date From</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Date To</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col md={12} className="d-flex gap-2 justify-content-center">
                            <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                <FontAwesomeIcon icon={faFilter} className="me-1" />
                                Apply
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                Clear
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card border="light" className="table-wrapper table-responsive shadow-sm">
                <Card.Body className="p-0" style={{ height: "70vh", overflow: "scroll" }}>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    ) : list.length > 0 ? (
                        <CustomTable fields={fields} data={list} />
                    ) : (
                        <div className="text-center p-5">
                            <FontAwesomeIcon icon={faFileAlt} size="3x" className="text-muted mb-3" />
                            <h5 className="text-muted">No Schedules Found</h5>
                            <p className="text-muted">
                                {appliedSearch || appliedStatus || appliedMda || appliedDateFrom || appliedDateTo
                                    ? "No schedules match your current filters. Try adjusting your search criteria."
                                    : "No payment schedules are available in the system."}
                            </p>
                        </div>
                    )}
                </Card.Body>
                {renderPagination()}
            </Card>

            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                        <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" />
                        Payment Schedule Journey - {selectedSchedule?.batch_id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSchedule && (
                        <>
                            <Card className="mb-4 border-0 shadow-sm">
                                <Card.Body className="p-4 bg-light">
                                    <Row>
                                        <Col md={4}>
                                            <div className="small text-muted text-uppercase fw-bold">Batch ID</div>
                                            <div className="h6 text-primary">{selectedSchedule.batch_id}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="small text-muted text-uppercase fw-bold">Contractor</div>
                                            <div className="h6">{selectedSchedule.contractor}</div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="small text-muted text-uppercase fw-bold">Total Amount</div>
                                            <div className="h6 text-success">₦{formatNumber(selectedSchedule.amount)}</div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {selectedSchedule.status_history && selectedSchedule.status_history.length > 0 && (
                                <div className="progress-indicator mb-4">
                                    <h6 className="mb-3 fw-bold">Processing Progress</h6>
                                    <div className="d-flex justify-content-between">
                                        {getProgressSteps(selectedSchedule.status_history).map((step, idx) => (
                                            <div key={idx} className={`text-center flex-fill ${step.status === 'pending' ? 'text-muted' : 'text-primary'}`}>
                                                <div className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2`}
                                                    style={{ width: '40px', height: '40px', border: `2px solid ${step.status === 'pending' ? '#dee2e6' : '#262b40'}`, background: step.status === 'current' ? '#262b40' : 'transparent', color: step.status === 'current' ? 'white' : 'inherit' }}>
                                                    <FontAwesomeIcon icon={step.icon} />
                                                </div>
                                                <div className="small fw-bold">{step.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <h6 className="mb-3 fw-bold">Detailed History</h6>
                            <div className="timeline">
                                {selectedSchedule.status_history?.map((h, i) => {
                                    const info = getStatusInfo(h.status);
                                    return (
                                        <div key={i} className="mb-4 border-start border-4 border-primary ps-3">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <Badge bg={info.type} className="mb-2">{h.status}</Badge>
                                                    <div className="fw-bold">{info.title}</div>
                                                    <div className="text-muted small">{info.description}</div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="small fw-bold">{moment(h.updated_at).format("DD MMM YYYY, HH:mm")}</div>
                                                    <div className="small text-muted">{moment(h.updated_at).fromNow()}</div>
                                                </div>
                                            </div>
                                            <div className="mt-2 d-flex align-items-center small">
                                                <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                                                <span className="fw-bold">{h.updated_by_name || 'System'}</span>
                                            </div>
                                            {h.remarks && (
                                                <div className="mt-2 p-2 bg-light rounded small italic">
                                                    <strong>Remarks:</strong> {h.remarks}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
        .timeline { position: relative; }
        .progress-indicator { padding: 20px; background: #f8f9fa; border-radius: 8px; }
      `}</style>
        </>
    );
}

export default AllPaymentScheduleHistory;
